import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, photoUrl, errText } from "../../lib/api";
import { priceLabel } from "../../lib/constants";
import { useLanguage } from "../../context/LanguageContext";
import { ListingCard } from "../ListingCard";
import { Button } from "../ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog";
import { toast } from "sonner";
import { LayoutGrid, Heart, Trash2, Pencil, Eye, Clock, BellRing, Search } from "lucide-react";
import { Empty, Loader } from "./shared";
import { lookingText } from "../IsoDemandTag";

export function MyListings({ navigate }) {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState(null);
  const [iso, setIso] = useState({});
  const load = () => api.get("/my/listings").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/my/iso-demand").then((r) => setIso(r.data)).catch(() => {}); }, []);

  const del = async (id) => {
    try { await api.delete(`/listings/${id}`); toast.success(t("d_listing_removed")); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const renew = async (id) => {
    try { await api.post(`/listings/${id}/renew`); toast.success(t("d_renewed")); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  if (!items) return <Loader />;
  if (!items.length) return <Empty icon={LayoutGrid} text={t("d_no_listings")} cta={t("d_post_first")} onCta={() => navigate("/post")} />;

  return (
    <div className="space-y-3">
      {items.map((l) => (
        <div key={l.id} className="bg-white rounded-2xl border border-slate-200/80 p-3 flex items-center gap-4" data-testid={`my-listing-${l.id}`}>
          <img src={photoUrl((l.photos || [])[0])} alt="" className="h-20 w-24 rounded-xl object-cover bg-slate-100 shrink-0" />
          <div className="flex-1 min-w-0">
            <Link to={`/listing/${l.id}`} className="font-semibold text-[#12234A] hover:text-[#F5551E] truncate block">{l.title}</Link>
            <p className="text-[#F5551E] font-bold text-sm">{priceLabel(l)}</p>
            {iso[l.id] > 0 && (
              <Link to={`/listing/${l.id}`} data-testid={`my-listing-iso-${l.id}`} className="mt-1 inline-flex items-center gap-1.5 bg-[#FFC233] text-[#12234A] text-xs font-bold px-2.5 py-1 rounded-full hover:brightness-95 transition-[filter]">
                <Search size={12} strokeWidth={3} /> {lookingText(iso[l.id], lang === "fr")}
              </Link>
            )}
            <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5 flex-wrap">
              <span className="flex items-center gap-1"><Eye size={12} /> {l.views} {t("d_views")}</span>
              {l.alerted_count != null && (
                <span className="inline-flex items-center gap-1 text-red-600 font-semibold" title={t("al_hint")} data-testid={`alerted-count-${l.id}`}>
                  · <BellRing size={12} /> {l.alerted_count === 0 ? t("al_count_zero") : l.alerted_count === 1 ? t("al_count_one") : t("al_count").replace("{n}", l.alerted_count)}
                </span>
              )}
              · {l.city}, {l.province}
              {l.status === "pending" ? (
                <span className="inline-flex items-center gap-1 text-amber-600 font-semibold" data-testid={`pending-tag-${l.id}`}><Clock size={12} /> {t("d_pending")}</span>
              ) : l.status === "rejected" ? (
                <span className="inline-flex items-center gap-1 text-red-600 font-semibold" data-testid={`rejected-tag-${l.id}`}><Clock size={12} /> {t("d_not_approved")}</span>
              ) : l.is_expired ? (
                <span className="inline-flex items-center gap-1 text-red-600 font-semibold" data-testid={`expired-tag-${l.id}`}><Clock size={12} /> {t("d_expired")}</span>
              ) : l.days_left != null && (
                <span className="inline-flex items-center gap-1 text-slate-500"><Clock size={12} /> {l.days_left}{t("d_days_left")}</span>
              )}
              {l.has_pending_edit && (
                <span className="inline-flex items-center gap-1 text-amber-600 font-semibold" data-testid={`edit-pending-tag-${l.id}`}><Clock size={12} /> {t("d_edit_pending")}</span>
              )}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            {l.is_expired && (
              <Button size="sm" onClick={() => renew(l.id)} data-testid={`renew-${l.id}`} className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5"><Clock size={15} /> {t("d_renew")}</Button>
            )}
            <Button variant="outline" size="icon" onClick={() => navigate(`/edit/${l.id}`)} data-testid={`edit-${l.id}`} className="rounded-xl"><Pencil size={16} /></Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="icon" data-testid={`delete-${l.id}`} className="rounded-xl text-red-600 hover:bg-red-50"><Trash2 size={16} /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader><AlertDialogTitle>{t("d_delete_listing_q")}</AlertDialogTitle><AlertDialogDescription>{t("d_delete_listing_desc")}</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-xl">{t("d_cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={() => del(l.id)} data-testid={`confirm-delete-${l.id}`} className="rounded-xl bg-red-600 hover:bg-red-700">{t("d_delete")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      ))}
    </div>
  );
}

export function Favorites() {
  const { t } = useLanguage();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/favorites").then((r) => setItems(r.data)); }, []);
  if (!items) return <Loader />;
  if (!items.length) return <Empty icon={Heart} text={t("d_no_favorites")} />;
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{items.map((l) => <ListingCard key={l.id} listing={l} />)}</div>;
}
